import { useState, useEffect } from "react";
import { useApp } from "@/contexts/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Share2, Copy, Check, Code, ExternalLink } from "lucide-react";

const TWITTER_ICON = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
);

const DISCORD_ICON = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.947 2.418-2.157 2.418z"/></svg>
);

const REDDIT_ICON = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/></svg>
);

const FACEBOOK_ICON = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
);

export function ShareWorld() {
  const ctx = useApp();
  const [copied, setCopied] = useState(false);
  const [copiedEmbed, setCopiedEmbed] = useState(false);

  const open = ctx.showShareDialog;
  const world = ctx.currentWorld;
  const info = ctx.shareInfo;

  useEffect(() => {
    if (open && world) {
      ctx.fetchShareInfo(world.id);
    }
  }, [open, world?.id]);

  const shareUrl = info.share_token
    ? `${window.location.origin}?share=${info.share_token}`
    : "";

  const embedCode = info.share_token
    ? `<iframe src="${window.location.origin}?embed=${info.share_token}" width="600" height="400" frameborder="0" style="border-radius:12px;border:1px solid #1e293b;" allowfullscreen></iframe>`
    : "";

  const handleCopy = (text, setter) => {
    navigator.clipboard.writeText(text);
    setter(true);
    setTimeout(() => setter(false), 2000);
  };

  const tweetText = world
    ? `Check out "${world.name}" — a world I built with Hyt Orbis World Builder! ${shareUrl}`
    : "";

  return (
    <Dialog open={open} onOpenChange={ctx.setShowShareDialog}>
      <DialogContent className="share-dialog" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="share-dialog-title">
            <Share2 size={20} /> Share World
          </DialogTitle>
          <DialogDescription className="share-dialog-desc">
            Share "{world?.name}" with the world
          </DialogDescription>
        </DialogHeader>

        {!world ? (
          <div className="share-empty">Select a world first.</div>
        ) : (
          <div className="share-body">
            {/* Toggle */}
            <div className="share-toggle-row" data-testid="share-toggle-row">
              <div className="share-toggle-info">
                <Label className="share-toggle-label">Public sharing</Label>
                <span className="share-toggle-hint">Anyone with the link can view this world</span>
              </div>
              <Switch
                checked={info.share_enabled}
                onCheckedChange={() => ctx.toggleShare()}
                data-testid="share-toggle"
              />
            </div>

            {info.share_enabled && info.share_token && (
              <>
                {/* Copy link */}
                <div className="share-link-row" data-testid="share-link-row">
                  <input
                    className="share-link-input"
                    value={shareUrl}
                    readOnly
                    data-testid="share-link-input"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopy(shareUrl, setCopied)}
                    data-testid="share-copy-btn"
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    {copied ? "Copied!" : "Copy"}
                  </Button>
                </div>

                {/* Social share buttons */}
                <div className="share-socials">
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-social-btn share-facebook"
                    data-testid="share-facebook"
                  >
                    <FACEBOOK_ICON /> Facebook
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-social-btn share-twitter"
                    data-testid="share-twitter"
                  >
                    <TWITTER_ICON /> Twitter / X
                  </a>
                  <a
                    href={`https://discord.com/channels/@me`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-social-btn share-discord"
                    onClick={() => handleCopy(shareUrl, setCopied)}
                    data-testid="share-discord"
                  >
                    <DISCORD_ICON /> Discord
                  </a>
                  <a
                    href={`https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(`Check out my world "${world.name}" on Hyt Orbis!`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-social-btn share-reddit"
                    data-testid="share-reddit"
                  >
                    <REDDIT_ICON /> Reddit
                  </a>
                </div>

                {/* Embed code */}
                <div className="share-embed-section">
                  <div className="share-embed-header">
                    <Code size={14} />
                    <span>Embed Code</span>
                  </div>
                  <div className="share-embed-row">
                    <code className="share-embed-code" data-testid="share-embed-code">{embedCode}</code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopy(embedCode, setCopiedEmbed)}
                      data-testid="share-copy-embed"
                    >
                      {copiedEmbed ? <Check size={14} /> : <Copy size={14} />}
                    </Button>
                  </div>
                </div>

                {/* Preview link */}
                <a
                  href={shareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="share-preview-link"
                  data-testid="share-preview-link"
                >
                  <ExternalLink size={14} /> Preview shared page
                </a>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
